import { useOwnedStations } from '@app/core/stations/hooks/useOwnedStations';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { socialExport } from '@mi-front/api-clients';
import { Station } from '@mi-front/core';
import { NoResults } from '@mi-front/ui';
import { Block, CheckCircle, LinkOff } from '@mui/icons-material';
import { Box, Tab, Tabs } from '@mui/material';
import { QueryView } from '@ui/utils/async-view/QueryView';
import sortBy from 'lodash/sortBy';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isSocialExportEnabledForStation } from '../../hooks/useSocialExportEnabledForStation';
import { NonConnectedStationFeature } from './NonConnectedStationFeature';
import { StationFeature } from './StationFeature';

interface Props {
  platform: SocialPlatform;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`stations-tabpanel-${index}`}
      aria-labelledby={`stations-tab-${index}`}
      {...other}
      sx={{
        pt: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {value === index && children}
    </Box>
  );
}

function categorizeStations(
  stations: readonly Station[],
  platform: SocialPlatform,
  connectionData: { results: Array<{ stationId: string; connected: boolean }> },
) {
  const sortedStations = sortBy(stations, 'name');

  const activeStations = sortedStations.filter((station) => {
    if (!isSocialExportEnabledForStation(station, platform)) return false;
    const state = connectionData.results.find((r) => r.stationId === station.id);
    return state?.connected === true;
  });

  const inactiveStations = sortedStations.filter((station) => {
    if (!isSocialExportEnabledForStation(station, platform)) return false;
    const state = connectionData.results.find((r) => r.stationId === station.id);
    return !state || state.connected === false;
  });

  const disabledStations = sortedStations.filter(
    (station) => !isSocialExportEnabledForStation(station, platform),
  );

  return { activeStations, inactiveStations, disabledStations };
}

export function StationsState(p: Props) {
  const query = socialExport.useListByPlatformConnection(p.platform.id);
  const stations = useOwnedStations();
  const [tabValue, setTabValue] = useState(0);
  const { $t } = useIntl();

  // Compute tabs based on query data
  const tabs = useMemo(() => {
    if (!query.data || !stations.length) return [];

    const { activeStations, inactiveStations, disabledStations } = categorizeStations(
      stations,
      p.platform,
      query.data,
    );

    return [
      {
        id: 'active',
        icon: <CheckCircle />,
        label: $t({
          id: 'socialExport.stationsState.active',
          defaultMessage: 'Activas',
        }),
        stations: activeStations,
        component: StationFeature,
      },
      {
        id: 'inactive',
        icon: <LinkOff />,
        label: $t({
          id: 'socialExport.stationsState.inactive',
          defaultMessage: 'Inactivas',
        }),
        stations: inactiveStations,
        component: StationFeature,
      },
      {
        id: 'disabled',
        icon: <Block />,
        label: $t({
          id: 'socialExport.stationsState.disabled',
          defaultMessage: 'Deshabilitadas',
        }),
        stations: disabledStations,
        component: NonConnectedStationFeature,
      },
    ].filter((tab) => tab.stations.length > 0);
  }, [query.data, stations, p.platform, $t]);

  // Reset tab value if current tab is out of bounds
  useEffect(() => {
    if (tabs.length > 0 && tabValue >= tabs.length) {
      setTabValue(0);
    }
  }, [tabs.length, tabValue]);

  return (
    <QueryView inline query={query}>
      {(data: socialExport.ListByPlatformConnection200) => {
        if (!stations.length) {
          return (
            <NoResults>
              <FormattedMessage
                id="socialExport.noStationsEnabledForFeature"
                defaultMessage="No se encontraron emisoras habilitadas para este feature"
              />
            </NoResults>
          );
        }

        if (tabs.length === 0) {
          return (
            <NoResults>
              <FormattedMessage
                id="socialExport.noStationsEnabledForFeature"
                defaultMessage="No se encontraron emisoras habilitadas para este feature"
              />
            </NoResults>
          );
        }

        const currentTab = Math.min(tabValue, tabs.length - 1);

        return (
          <Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={currentTab} onChange={(_event, newValue) => setTabValue(newValue)}>
                {tabs.map((tab, index) => (
                  <Tab
                    key={tab.id}
                    icon={tab.icon}
                    iconPosition="start"
                    label={`${tab.label} (${tab.stations.length})`}
                    id={`stations-tab-${index}`}
                    aria-controls={`stations-tabpanel-${index}`}
                  />
                ))}
              </Tabs>
            </Box>
            {tabs.map((tab, index) => {
              const Component = tab.component;
              return (
                <TabPanel key={tab.id} value={currentTab} index={index}>
                  {tab.stations.map((station) => (
                    <Component
                      key={station.id}
                      platform={p.platform}
                      station={station}
                      state={data.results.find(
                        (r: socialExport.GetConnectionStateDto) => r.stationId === station.id,
                      )}
                    />
                  ))}
                </TabPanel>
              );
            })}
          </Box>
        );
      }}
    </QueryView>
  );
}
